/* eslint-disable no-undef */
import React, { useState, useEffect } from 'react'
import { Paper, makeStyles, Grid, Box, Table, TableHead, TableBody, TableRow, TableCell } from '@material-ui/core'
import ExtraElement from '../ExtraElements/ExtraElement'
import { fetchResearches } from '../../../../api'
import moment from 'moment'

/**
 * Generate a consistent color based on the winner's name
 * This ensures the same person always gets the same avatar color
 */
const getAvatarColor = name => {
    const colors = [
        '#205a40', // Primary green
        '#2d7a56', // Lighter green
        '#1a4a34', // Darker green
        '#3d8b6a', // Sage green
        '#4a9d7a', // Mint
        '#167d5c', // Teal green
        '#2e6b4f', // Forest
        '#3a7860'  // Moss
    ]
    let hash = 0
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash)
    }
    return colors[Math.abs(hash) % colors.length]
}

/**
 * Get initials from a name (max 2 characters)
 */
const getInitials = name => {
    if (!name) return '?'
    const parts = name.trim().split(' ')
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
}

const useStyles = makeStyles(theme => ({
    root: {
        color: '#4a5d52',
        textAlign: 'center',
        letterSpacing: 0.3,
        fontSize: '0.85rem',
        margin: 'auto',
        fontFamily: '"Plus Jakarta Sans", sans-serif'
    },
    paper: {
        background: '#ffffff',
        boxShadow: '0 4px 20px rgba(32, 90, 64, 0.08), 0 8px 40px rgba(32, 90, 64, 0.04)',
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
        borderRadius: '20px!important',
        border: '1px solid rgba(32, 90, 64, 0.06)',
        [theme.breakpoints.down(600)]: {
            borderRadius: '0!important',
            borderLeft: 'none',
            borderRight: 'none'
        }
    },
    title: {
        fontWeight: 700,
        fontSize: '1.15rem',
        lineHeight: '1.4rem',
        color: '#1a2e23',
        fontFamily: '"Plus Jakarta Sans", sans-serif'
    },
    tableWrapper: {
        width: '100%',
        overflowX: 'auto'
    },
    table: {
        fontFamily: '"Plus Jakarta Sans", sans-serif',
        '& td': {
            whiteSpace: 'nowrap',
            paddingTop: 12,
            paddingBottom: 12,
            color: '#4a5d52',
            fontSize: '0.85rem',
            verticalAlign: 'middle'
        },
        '& tbody tr:last-child td, & tbody tr:last-child th': {
            border: 0
        },
        '& tbody tr': {
            transition: 'background 0.2s ease',
            '&:hover': {
                background: 'rgba(32, 90, 64, 0.03)'
            }
        },
        '& th': {
            fontSize: '0.7rem',
            fontWeight: 600,
            color: '#7a8a82',
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
            paddingBottom: 8
        }
    },
    dateCell: {
        width: '45%',
        [theme.breakpoints.down('sm')]: {
            width: 'auto'
        }
    },
    dateText: {
        fontSize: '0.8rem',
        fontWeight: 600,
        color: '#1a2e23',
        marginBottom: 2
    },
    titleCell: {
        whiteSpace: 'normal!important',
        fontWeight: 400,
        color: '#5a6d62!important',
        fontSize: '0.8rem',
        lineHeight: 1.3
    },
    winnerCell: {
        display: 'flex',
        alignItems: 'center',
        gap: 12
    },
    avatar: {
        width: 36,
        height: 36,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ffffff',
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.5px',
        flexShrink: 0,
        boxShadow: '0 2px 8px rgba(32, 90, 64, 0.15)'
    },
    winnerInfo: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0
    },
    priceLabel: {
        maxWidth: 220,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontWeight: 600,
        fontSize: '0.82rem',
        color: '#205a40!important',
        marginBottom: 2
    },
    winnerName: {
        fontSize: '0.78rem',
        color: '#6a7d72',
        fontWeight: 400
    },
    headerSubtext: {
        fontWeight: 400,
        color: '#6a7d72',
        fontSize: '0.88rem',
        lineHeight: 1.5,
        marginTop: 4
    }
}))

function Research({ showSlogansLast = false, researches: researchesProp = null }) {
    const [researchesState, setResearchesState] = useState([])

    const classes = useStyles()

    // Use prop if provided, otherwise use internal state
    const researches = researchesProp ?? researchesState

    useEffect(() => {
        // Skip fetch if researches are provided via prop
        if (researchesProp !== null) return

        fetchResearches().then(response => {
            if (response.data) {
                setResearchesState(response.data)
            }
        })
    }, [researchesProp])

    const renderSlogans = (
        <Paper className={classes.paper + ' extra-element-paper'} elevation={1}>
            <Box px={{ xs: 2, sm: 3 }} pb={0.5} pt={2}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                        <ExtraElement
                            imageSrc={'/img/2023-ssl.png'}
                            title={'Osallistuminen on turvallista ja vie vain hetken.'}
                            description={'Business Finland tukee tuote&shy;kehitystämme. Tutkimuksemme ovat tehokkaita ja turvallisia.'}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <ExtraElement
                            imageSrc={'/img/2023-asml.png'}
                            title={'Suomen Asiakkuus­markkinointi­liiton jäsen.'}
                            description={'Olemme ASML:n jäsen ja hyödynnämme tietosuoja- ja kuluttaja-asioissa heidän ohjeistuksiaan.'}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <ExtraElement
                            imageSrc={'/img/2023-avainlippu.png'}
                            title={'Olemme tunnettu täysin suomalaisena ja luotettavana toimijana.'}
                            description={'Vastauksiasi ei jaeta ulkopuolisille ilman, että olet antanut siihen luvan. Lisätietoja käyttöehdoissa.'}
                        />
                    </Grid>
                </Grid>
            </Box>
            <Box px={{ xs: 2, sm: 3 }} pb={{ xs: 3, sm: 2.5 }} pt={{ xs: 4, sm: 2.5 }}>
                <Box fontWeight={400} fontSize="0.9rem" color="#5a6d62">
                    Tutustu toimintaamme ja lue meistä lisää{' '}
                    <a 
                        href="https://www.leadspark.fi" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#205a40', fontWeight: 500, textDecoration: 'none' }}
                    >
                        yrityksemme verkkosivuilla
                    </a>
                    .
                </Box>
            </Box>
        </Paper>
    )

    const renderResearches = researches?.length > 0 && (
        <Paper className={classes.paper + ' extra-element-paper'} elevation={1}>
            <Box px={{ xs: 2, sm: 3 }} pt={3} pb={1.5}>
                <Box className={classes.title + ' extra-element-title'}>
                    Aikaisemmat tutkimukset
                </Box>
                <Box className={classes.headerSubtext}>
                    Tutkimuksen palkinnon arvonta suoritetaan heti tutkimuksen päätyttyä. Listassa viimeisimmät 20 Tutkimuskeskuksen tekemää
                    tutkimusta.
                </Box>
            </Box>
            <Box px={{ xs: 1, sm: 1.5 }} pb={1.5} className={classes.tableWrapper}>
                <Table className={classes.table} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell className={classes.dateCell}>Aika ja kohde</TableCell>
                            <TableCell>Palkinto ja voittaja</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {researches.map(research => (
                            <TableRow key={research.id}>
                                <TableCell className={classes.dateCell}>
                                    <div className={classes.dateText}>
                                        {moment(research.starts_at).format('DD.MM.')} – {moment(research.ends_at).format('DD.MM.YYYY')}
                                    </div>
                                    <div className={classes.titleCell}>
                                        {research.title}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <div className={classes.winnerCell}>
                                        <div
                                            className={classes.avatar}
                                            style={{ backgroundColor: getAvatarColor(research.winner_name || '') }}
                                        >
                                            {getInitials(research.winner_name)}
                                        </div>
                                        <div className={classes.winnerInfo}>
                                            <div className={classes.priceLabel}>{research.price}</div>
                                            <div className={classes.winnerName}>
                                                {research.winner_name} ({research.winner_from})
                                            </div>
                                        </div>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        </Paper>
    )

    return <>{showSlogansLast ? [renderResearches, renderSlogans] : [renderSlogans, renderResearches]}</>
}

export default Research
